/**
 * The viewer filters a dashboard is read under: a period, a set of premises and
 * a comparison, held in the route's search params and sent as run-time
 * `overrides`. Nothing here is saved.
 *
 * The module owns NO clock and does NO window arithmetic. Every preset travels
 * as a `ReportRangeValue` SPEC the SERVER resolves in the org's zone, so a
 * reader in another zone and the engine agree on what "the last 30 days" means.
 * The single exception is `last_year`, which needs a calendar year number the
 * wire has no "previous year" form for: it is read off an instant the CALLER
 * injects, rendered in the catalog's zone — the one `Intl` call and the one
 * subtraction here, and the reason `Clock` is a parameter rather than a read.
 */

import { type components } from "@njbsoft/types";

type ReportRangeValue = components["schemas"]["ReportRangeValue"];
type ReportDashboardRunOverrides = components["schemas"]["ReportDashboardRunOverrides"];

export type PeriodPreset = "30d" | "90d" | "12m" | "24m" | "this_year" | "last_year";

export const PERIOD_PRESETS: readonly PeriodPreset[] = [
  "30d",
  "90d",
  "12m",
  "24m",
  "this_year",
  "last_year",
];

/** Frozen by the wire: the two prior windows a number card can be compared against. */
export type CompareMode = components["schemas"]["ReportDashboardCompareMode"];

export const COMPARE_MODES: readonly CompareMode[] = ["previous_period", "same_period_last_year"];

/** `ReportRunOverrides.facility_ids` admits at most this many ids. */
export const FACILITY_CEILING = 100;

/** The filters a viewer has set. `premises` is always an array; the rest are absent when unset. */
export interface ViewerFilters {
  period?: PeriodPreset;
  from?: string;
  to?: string;
  premises: string[];
  compare?: CompareMode;
}

/** The URL's own shape: one flat string per param, `premises` a comma list. */
export interface DashboardSearch {
  period?: PeriodPreset;
  from?: string;
  to?: string;
  premises?: string;
  compare?: CompareMode;
}

export const EMPTY_FILTERS: ViewerFilters = { premises: [] };

/** The instant `last_year` counts back from, and the zone it is read in. */
export interface Clock {
  now: Date;
  timeZone: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Every preset that needs no number, as the literal spec it travels as. Spread
 * on the way out so a caller can never edit the table by editing its result.
 */
const PRESET_SPECS: Record<Exclude<PeriodPreset, "last_year">, ReportRangeValue> = {
  "30d": { kind: "relative", unit: "day", n: 30 },
  "90d": { kind: "relative", unit: "day", n: 90 },
  "12m": { kind: "relative", unit: "month", n: 12 },
  "24m": { kind: "relative", unit: "month", n: 24 },
  this_year: { kind: "current", unit: "year" },
};

function parsePreset(value: unknown): PeriodPreset | undefined {
  if (typeof value !== "string") return undefined;
  return PERIOD_PRESETS.find((preset) => preset === value);
}

function parseCompare(value: unknown): CompareMode | undefined {
  if (typeof value !== "string") return undefined;
  return COMPARE_MODES.find((mode) => mode === value);
}

function parseDate(value: unknown): string | undefined {
  return typeof value === "string" && ISO_DATE.test(value) ? value : undefined;
}

/**
 * A comma list of facility ids, normalized: trimmed, uuid-shaped only,
 * de-duplicated in first-seen order, cut at the wire's ceiling. Anything else
 * a URL can carry — a name, a blank, a repeat — is dropped rather than sent.
 */
function parsePremises(value: unknown): string[] {
  if (typeof value !== "string") return [];
  const seen = new Set<string>();
  for (const entry of value.split(",")) {
    const id = entry.trim();
    if (!UUID_RE.test(id)) continue;
    seen.add(id);
    if (seen.size === FACILITY_CEILING) break;
  }
  return [...seen];
}

/**
 * The route's `validateSearch` body: an allow-list read of whatever the URL
 * carries, the `parseTab` idiom one entity up scaled to five params. A `period`
 * WINS over `from` / `to`, and a lone bound of a custom range is dropped — a
 * half-typed range never runs. The two dates' ORDER is not checked here: the
 * server refuses a reversed range with `overrides_invalid` and the page renders
 * that refusal, so one rule fences it rather than two.
 */
export function parseDashboardSearch(raw: Record<string, unknown>): DashboardSearch {
  const search: DashboardSearch = {};

  const period = parsePreset(raw.period);
  if (period !== undefined) {
    search.period = period;
  } else {
    const from = parseDate(raw.from);
    const to = parseDate(raw.to);
    if (from !== undefined && to !== undefined) {
      search.from = from;
      search.to = to;
    }
  }

  const premises = parsePremises(raw.premises);
  if (premises.length > 0) search.premises = premises.join(",");

  const compare = parseCompare(raw.compare);
  if (compare !== undefined) search.compare = compare;

  return search;
}

export function filtersFromSearch(search: DashboardSearch): ViewerFilters {
  const filters: ViewerFilters = { premises: parsePremises(search.premises) };

  if (search.period !== undefined) {
    filters.period = search.period;
  } else if (search.from !== undefined && search.to !== undefined) {
    filters.from = search.from;
    filters.to = search.to;
  }

  if (search.compare !== undefined) filters.compare = search.compare;

  return filters;
}

/** An unset member is ABSENT, never an empty string — the route then drops it from the URL. */
export function searchFromFilters(filters: ViewerFilters): DashboardSearch {
  const search: DashboardSearch = {};

  if (filters.period !== undefined) {
    search.period = filters.period;
  } else if (filters.from !== undefined && filters.to !== undefined) {
    search.from = filters.from;
    search.to = filters.to;
  }

  if (filters.premises.length > 0) search.premises = filters.premises.join(",");
  if (filters.compare !== undefined) search.compare = filters.compare;

  return search;
}

export function hasFilters(filters: ViewerFilters): boolean {
  return (
    filters.period !== undefined ||
    (filters.from !== undefined && filters.to !== undefined) ||
    filters.premises.length > 0 ||
    filters.compare !== undefined
  );
}

/**
 * Whether two filter sets say different things: the four scalars by value and
 * `premises` as a SET, because the order the ids were picked in is not a filter.
 * It is what a bar carrying its surface's own DEFAULTS measures "the reader has
 * moved away from them" with, where `hasFilters` — a comparison against the
 * empty set alone — cannot.
 */
export function filtersDiffer(a: ViewerFilters, b: ViewerFilters): boolean {
  if (a.period !== b.period || a.from !== b.from || a.to !== b.to || a.compare !== b.compare) {
    return true;
  }
  if (a.premises.length !== b.premises.length) return true;

  const held = new Set(a.premises);
  return b.premises.some((id) => !held.has(id));
}

/**
 * The window spec the run carries, or nothing when the viewer set no period.
 * `last_year` is the only entry that is not a constant: the wire's `year` form
 * needs the number, and the number is the calendar year the injected instant
 * falls in IN THE CATALOG'S ZONE — a Phoenix org reading the first hours of
 * January UTC is still in the old year, and comparing against the wrong year is
 * a silently wrong dashboard.
 */
export function periodSpec(filters: ViewerFilters, clock: Clock): ReportRangeValue | undefined {
  if (filters.period === "last_year") {
    const year = Number.parseInt(
      new Intl.DateTimeFormat("en-US", { timeZone: clock.timeZone, year: "numeric" }).format(
        clock.now,
      ),
      10,
    );
    return { kind: "year", year: year - 1 };
  }
  if (filters.period !== undefined) return { ...PRESET_SPECS[filters.period] };
  if (filters.from !== undefined && filters.to !== undefined) {
    return { kind: "absolute", from: filters.from, to: filters.to };
  }
  return undefined;
}

/**
 * The run's `overrides`, or nothing at all when the viewer set none — an absent
 * `overrides` member runs every card exactly as its author saved it.
 */
export function toOverrides(
  filters: ViewerFilters,
  clock: Clock,
): ReportDashboardRunOverrides | undefined {
  if (!hasFilters(filters)) return undefined;

  const overrides: ReportDashboardRunOverrides = {};
  const period = periodSpec(filters, clock);
  if (period !== undefined) overrides.period = period;
  if (filters.premises.length > 0) overrides.facility_ids = [...filters.premises];
  if (filters.compare !== undefined) overrides.compare = filters.compare;
  return overrides;
}
